<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell v-if="!showStartDate && !showReview" width="half">
                <form-field :label="$t('forms.auth.schedule.fields.location.label')">
                    <select-input
                        ref="chooseLocation"
                        class="schedule-apply__input"
                        :placeholder="$t('forms.auth.schedule.fields.location.placeholder')"
                        :hasAutofocus="true"
                        :options="locationOptions"
                        :value="currentLocationId"
                        @change="changeCurrentLocationId($event)"
                    ></select-input>
                </form-field>
            </form-cell>

            <form-cell v-if="!showReview && !showStartDate" :isDisabled="!currentLocationId" width="half">
                <select-field
                    class="schedule-apply__input"
                    :label="$t('forms.auth.schedule.fields.appointment_length.label')"
                    :placeholder="$t('forms.auth.schedule.fields.appointment_length.placeholder')"
                    :hint="$t('forms.auth.schedule.fields.appointment_length.hint')"
                    :options="appointmentLengthOptions"
                    :formData="locationSchedule(currentLocationId)"
                    valuePath="appointmentLength"
                    @change="changeLocationSchedule($event, currentLocationId)"
                ></select-field>
            </form-cell>
        </form-row>

        <form-row v-if="!showReview && !showStartDate">
            <form-cell :isDisabled="isFormDisabled">
                <div class="schedule">
                    <div class="schedule__container">
                        <div class="schedule__days">
                            <form-field :label="$t('forms.auth.schedule.fields.days.label')">
                                <schedule-days
                                    :currentDay="currentDay"
                                    :filledDays="filledDays(currentLocationId)"
                                    @click="changeCurrentDay($event)"
                                ></schedule-days>
                            </form-field>
                        </div>
                    </div>

                    <div class="schedule__timeline">
                        <schedule-timeline
                            :dayFrom="dayFrom"
                            :dayTo="dayTo"
                            :appointmentLength="currentAppointmentLength"
                            :formData="locationSchedule(currentLocationId)"
                            :valuePath="slotsForDayPath(currentDay)"
                            @change="changeLocationSchedule($event, currentLocationId)"
                        ></schedule-timeline>
                    </div>

                    <div class="schedule__regularity">
                        <div class="schedule__frequency">
                            <radio-field
                                :label="$t('forms.auth.schedule.fields.frequency.label')"
                                :isInline="false"
                                :options="frequencyOptions"
                                :formData="locationSchedule(currentLocationId)"
                                valuePath="frequency"
                                @change="changeAndApplyLocationSchedule($event, currentLocationId)"
                            ></radio-field>
                        </div>
                    </div>
                </div>
            </form-cell>
        </form-row>

        <form-row v-if="showReview">
            <form-cell :isDisabled="isFormDisabled">
                <schedule-summary
                    :locations="locations"
                    :schedule="data.appliedSchedule"
                    :isRegistration="true"
                    @discard="discardSchedule($event)"
                ></schedule-summary>
            </form-cell>
        </form-row>

        <form-row v-if="showStartDate">
            <form-cell :isDisabled="isStartDateDisabled">
                <div class="schedule__start-date registration-schedule-start-date">
                    <form-field :label="$t('forms.auth.schedule.fields.start_date.label')" :isInline="true">
                        <schedule-start-date
                            :frequency="currentFrequency"
                            :isRegistration="true"
                            @startWeekDateSelected="startWeekDateSelected"
                        ></schedule-start-date>
                    </form-field>
                </div>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons :isDisabled="isFormDisabled">
                <common-button
                    v-if="locationOptions.length > 2"
                    :label="$t('forms.auth.schedule.microcopy.set_another_location')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="setForAnotherLocation"
                ></common-button>
                <common-button
                    :label="
                        showReview
                            ? $t('forms.auth.schedule.microcopy.edit_timeline')
                            : $t('forms.auth.schedule.microcopy.review')
                    "
                    :isSubmittable="false"
                    :isDisabled="isReviewDisabled"
                    :isProcessing="isSending"
                    size="large"
                    type="primary"
                    @click="previewChanges()"
                ></common-button>
                <common-button
                    v-if="showReview && !showStartDate"
                    :label="'SET START DATE'"
                    :isSubmittable="false"
                    size="large"
                    type="primary"
                    @click="previewStartDate()"
                ></common-button>
                <common-button
                    v-if="!showReview && showStartDate"
                    :isProcessing="isSending"
                    :isDisabled="completeDisabled"
                    :label="$t('common.microcopy.complete')"
                    size="large"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>

        <common-modal v-if="showErrorModal" :showCloseButton="false" width="small" @close="emitEvent({ name: 'close' })">
            <common-content-spacer height="small">
                <common-warning size="medium" :text="warningMessage" :isCenter="true"></common-warning>
                <common-buttons>
                    <common-button
                        :label="$t('common.microcopy.continue')"
                        type="primary"
                        size="large"
                        :isSubmittable="false"
                        @click="emitSent()"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
        </common-modal>
    </form-wrapper>
</template>

<script>
    import dayjs from 'dayjs'
    import { importComponents } from '~/helpers'
    import ScheduleMixin from 'mixins/ScheduleMixin'
    import AdminRegistersPhysicianMixin from 'mixins/AdminRegistersPhysicianMixin'
    import UsersAPI from 'api/UsersAPI'
    import { generateStartDate } from './auth-schedule-form/ScheduleStartDate'

    export default {
        mixins: [ScheduleMixin, AdminRegistersPhysicianMixin],

        components: {
            ...importComponents(require.context('components/forms/auth-schedule-form')),
        },

        props: {
            dayFrom: { type: Number, required: true },
            dayTo: { type: Number, required: true },
        },

        data() {
            return {
                isShowingForm: true,
                currentLocationId: null,
                currentDay: 0,
                hasUnappliedSlots: false,
                data: {
                    appliedSchedule: this.createInitialSchedule(),
                    draftSchedule: this.createInitialSchedule(),
                },
                showReview: false,
                showStartDate: false,
                warningMessage: '',
                showErrorModal: false,
                isStartDateDisabled: undefined,
            }
        },

        computed: {
            frequencyOptions() {
                return ['weekly', 'biweekly'].map((value) => ({
                    label: this.$t(`forms.auth.schedule.fields.frequency.options.${value}`),
                    value,
                }))
            },
            appointmentLengthOptions() {
                return [15, 30, 60].map((value) => ({
                    label: this.$tc('forms.auth.schedule.fields.appointment_length.minutes', value),
                    value,
                }))
            },

            isFormDisabled() {
                return !this.currentLocationId || !this.locationSchedule(this.currentLocationId).appointmentLength
            },

            currentAppointmentLength() {
                return this.locationSchedule(this.currentLocationId).appointmentLength || 15
            },

            currentFrequency() {
                return this.locationSchedule(this.currentLocationId).frequency || 'weekly'
            },

            isReviewDisabled() {
                return !this.data.draftSchedule[Number(this.currentLocationId) || 0].days.filter((day) => day.length > 0).length
            },

            completeDisabled() {
                return !this.data.appliedSchedule[Number(this.currentLocationId) || 0].days.filter((day) => day.length > 0).length
            },

            isScheduleActionsDisabled() {
                const dayAppliedSchedule = this.getDayFromSchedule(true)
                const dayDraftSchedule = this.getDayFromSchedule()

                let isRepeatChanged = false
                if (
                    this.data.appliedSchedule[this.currentLocationId] &&
                    this.data.draftSchedule[this.currentLocationId] &&
                    this.data.appliedSchedule[this.currentLocationId].frequency !==
                        this.data.draftSchedule[this.currentLocationId].frequency
                ) {
                    isRepeatChanged = true
                }
                if (
                    this.currentLocationId === 'all' &&
                    this.data.appliedSchedule[0] &&
                    this.data.appliedSchedule[0].frequency !== this.currentFrequency
                ) {
                    isRepeatChanged = true
                }

                return (
                    !this.locationSlots(this.currentLocationId, this.currentDay).length ||
                    (dayAppliedSchedule === dayDraftSchedule && !isRepeatChanged)
                )
            },
        },

        methods: {
            previewChanges() {
                this.showReview = !this.showReview
                this.showStartDate = false
                if (this.hasUnappliedSlots) {
                    this.applyTemporaryScheduleChanges()
                    this.hasUnappliedSlots = false
                }
            },

            previewStartDate() {
                this.showReview = false
                this.showStartDate = true
            },

            startWeekDateSelected(startWeekDate) {
                const modifiedEvent = {
                    path: 'startWeekDate',
                    value: dayjs(startWeekDate).format('YYYY-MM-DD'),
                }
                this.changeAndApplyLocationSchedule(modifiedEvent, this.currentLocationId)
            },

            setForAnotherLocation() {
                if (this.hasUnappliedSlots) {
                    this.applyTemporaryScheduleChanges()
                    this.hasUnappliedSlots = false
                }
                this.showReview = false
                this.changeCurrentLocationId(null)
                this.$refs.chooseLocation?.focus()
            },

            applyTemporaryScheduleChanges() {
                this.applySchedule({
                    locationId: this.currentLocationId,
                    days: [this.currentDay],
                    slots: this.locationSlots(this.currentLocationId, this.currentDay),
                })
            },

            getDayFromSchedule(inAppliedSchedule = false) {
                return this.data[inAppliedSchedule ? 'appliedSchedule' : 'draftSchedule'][Number(this.currentLocationId) || 0]
                    .days[this.currentDay]
            },

            createInitialSchedule() {
                const defaultFrequency = 'weekly'
                return this.locations.reduce((schedule, location) => {
                    schedule[location.value] = {
                        appointmentLength: undefined,
                        frequency: defaultFrequency,
                        days: Array(7).fill([]),
                        startWeekDate: generateStartDate(defaultFrequency).format('YYYY-MM-DD'),
                    }
                    return schedule
                }, {})
            },

            changeCurrentLocationId(newLocationId) {
                this.currentLocationId = newLocationId
            },

            changeCurrentDay(newCurrentDay) {
                this.highlightTimeline()
                if (this.hasUnappliedSlots) {
                    this.applyTemporaryScheduleChanges()
                    this.hasUnappliedSlots = false
                }
                this.currentDay = newCurrentDay
            },

            highlightTimeline() {
                const el = document.getElementsByClassName('schedule__timeline')[0]
                el.classList.add('apply-shake')
                el.addEventListener('animationend', () => {
                    el.classList.remove('apply-shake')
                })
            },

            discardSchedule({ locationId, days }) {
                days.forEach((day) => {
                    this.discardDay(locationId, day)
                    this.discardDay(locationId, day, true)
                })
            },
            applySchedule({ locationId, days, slots: value }) {
                days.forEach((day) => {
                    const data = { path: this.slotsForDayPath(day), value }
                    this.changeLocationSchedule(data, locationId)
                    this.changeLocationSchedule(data, locationId, true)
                })
                // Also sync appointmentLength and frequency in draft and applied.
                this.changeLocationSchedule(
                    {
                        path: 'appointmentLength',
                        value: this.locationSchedule(locationId).appointmentLength,
                    },
                    locationId,
                    true,
                )
                this.changeLocationSchedule(
                    {
                        path: 'frequency',
                        value: this.locationSchedule(locationId).frequency,
                    },
                    locationId,
                    true,
                )
                this.changeLocationSchedule(
                    {
                        path: 'startWeekDate',
                        value: this.locationSchedule(locationId).startWeekDate,
                    },
                    locationId,
                    true,
                )

                this.hasUnappliedSlots = false
            },
            handleFormSubmission({ appliedSchedule }) {
                this.isStartDateDisabled = true
                const { id: userId } = this.$auth.getActiveMember()
                const { id: practiceId } = this.$auth.getPractice()
                const request = UsersAPI.setSchedule(userId, practiceId, appliedSchedule)
                this.handleRequest(request)
                    .then(() => {
                        if (this.isAdminRegisteringPhysician) {
                            this.completePhysicianRegistration(true)
                        }
                        this.emitSent()
                    })
                    .catch((error) => {
                        if (error.response?.data?.subCode === 'user.availability') {
                            if (this.isAdminRegisteringPhysician) {
                                this.completePhysicianRegistration(true)
                            }
                            this.warningMessage = error.response.data.message
                            this.showErrorModal = true
                        }
                    })
            },
        },
        watch: {
            showReview() {
                this.$emit('show-review-changed', this.showReview)
            },

            showStartDate() {
                this.$emit('show-start-changed', this.showStartDate)
            },
        },
        mounted() {
            this.$on('local:afterChange:draftSchedule.*.appointmentLength', () => {
                this.discardDays(this.currentLocationId)
                this.discardDays(this.currentLocationId, true)
            })
            this.$on('local:afterChange:draftSchedule.*.days.*', ({ value }) => {
                this.hasUnappliedSlots = value.length
            })
        },
    }
</script>

<style>
    .registration-schedule-start-date label {
        margin-top: auto !important;
        margin-bottom: auto !important;
    }
</style>
